import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

export const RH_CHAIN_ID = parseInt(process.env.REACT_APP_RH_CHAIN_ID);
export const BLOCKSCOUT_URL = process.env.REACT_APP_BLOCKSCOUT_URL;

const MAINNET = {
  chainId: "0x" + RH_CHAIN_ID.toString(16),
  chainName: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: [process.env.REACT_APP_RH_RPC_URL],
  blockExplorerUrls: [BLOCKSCOUT_URL],
};

const WalletCtx = createContext(null);

export function WalletProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [balance, setBalance] = useState(null);

  const eth = typeof window !== "undefined" ? window.ethereum : null;

  const refreshBalance = useCallback(async (acct) => {
    if (!eth || !acct) return;
    try {
      const wei = await eth.request({ method: "eth_getBalance", params: [acct, "latest"] });
      setBalance(parseInt(wei, 16) / 1e18);
    } catch { /* noop */ }
  }, [eth]);

  useEffect(() => {
    if (!eth) return;
    // Never call the provider on page load — wallet opens ONLY via the Connect Wallet button.
    const onAccounts = (accts) => { setAccount(accts?.[0] || null); if (accts?.[0]) refreshBalance(accts[0]); };
    const onChain = (c) => setChainId(parseInt(c, 16));
    eth.on?.("accountsChanged", onAccounts);
    eth.on?.("chainChanged", onChain);
    return () => {
      eth.removeListener?.("accountsChanged", onAccounts);
      eth.removeListener?.("chainChanged", onChain);
    };
  }, [eth, refreshBalance]);

  const connect = async () => {
    if (!eth) {
      toast.error("No EVM wallet detected. Install MetaMask to connect.");
      return;
    }
    try {
      const accts = await eth.request({ method: "eth_requestAccounts" });
      setAccount(accts[0]);
      refreshBalance(accts[0]);
      const c = await eth.request({ method: "eth_chainId" });
      setChainId(parseInt(c, 16));
      toast.success("Wallet connected");
    } catch (e) {
      if (e?.code !== 4001) toast.error("Connection failed");
    }
  };

  const switchChain = async () => {
    if (!eth) return;
    try {
      await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: MAINNET.chainId }] });
    } catch (e) {
      if (e?.code === 4902) {
        try {
          await eth.request({ method: "wallet_addEthereumChain", params: [MAINNET] });
        } catch { toast.error("Could not add Robinhood Chain"); }
      }
    }
  };

  const disconnect = () => { setAccount(null); setBalance(null); toast("Disconnected"); };

  return (
    <WalletCtx.Provider value={{ account, chainId, balance, connect, disconnect, switchChain, hasWallet: !!eth }}>
      {children}
    </WalletCtx.Provider>
  );
}

export const useWallet = () => useContext(WalletCtx);
